import { X, type LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { UploadItemTypeEnum } from '@/Domain/enum/UploadItemType.enum';
import { UploadStatusEnum } from '@/Domain/enum/UploadStatus.enum';
import { UploadFileHelper } from '@/Domain/helper/UploadFile.helper';

type Props = {
  name: string;
  size: number;
  error?: string;
  status: UploadStatusEnum;
  index: number;
  onRemove?: (index: number) => void;
  getFileIcon: (fileName: string, fileType?: UploadItemTypeEnum) => LucideIcon;
  getFileSizeText: (size: number) => string;
  showStatus?: boolean;
  showRemoveButton?: boolean;
  fileType?: UploadItemTypeEnum;
};

// Matches the Google Favicon API used by `prefetch_favicon_url` in the backend
// (appquickchat/api/accounts/onboarding_from_url_helpers.py). Kept in sync so
// the URL-upload widget renders the same icon the onboarding flow picks.
const faviconUrlFromName = (rawName: string): string | null => {
  // Representative URLs for multi-URL submits look like "https://example.com +4".
  const firstToken = rawName.trim().split(/\s+/)[0];
  try {
    const { hostname } = new URL(firstToken);
    return `https://www.google.com/s2/favicons?domain=${hostname}&sz=128`;
  } catch {
    return null;
  }
};

/**
 * Split a representative-URL name like `"https://example.com/pricing +4"`
 * into `[displayUrl, extraCount]` so the renderer can keep the count badge
 * visible with `shrink-0` while the URL itself truncates with ellipsis.
 * Falls back to `[name, null]` for plain URLs and for file names.
 */
const splitRepresentativeName = (rawName: string): [string, string | null] => {
  const match = rawName.match(/^(.+?)\s\+(\d+)$/);
  if (!match) return [rawName, null];
  return [match[1], match[2]];
};

export const FileItem = ({
  name,
  size,
  error,
  status,
  index,
  onRemove,
  getFileIcon,
  getFileSizeText,
  showStatus = false,
  showRemoveButton = true,
  fileType = UploadItemTypeEnum.FILE,
}: Props) => {
  const isPending = UploadFileHelper.isPendingStatus(status);
  const FileIcon = getFileIcon(name, fileType);
  const statusIconConfig = showStatus ? UploadFileHelper.getStatusIconConfig(status) : null;
  const StatusIcon = statusIconConfig?.icon;

  const faviconUrl = fileType === UploadItemTypeEnum.URL ? faviconUrlFromName(name) : null;
  const [faviconFailed, setFaviconFailed] = useState(false);
  const showFavicon = faviconUrl !== null && !faviconFailed;
  const [displayName, extraCount] =
    fileType === UploadItemTypeEnum.URL ? splitRepresentativeName(name) : [name, null];

  const statusIcon = statusIconConfig ? (
    <span className="flex items-center justify-center w-6 h-6">
      {statusIconConfig.animate ? (
        <Spinner className={`h-5 w-5 ${statusIconConfig.className}`} />
      ) : StatusIcon ? (
        <StatusIcon className={`h-5 w-5 ${statusIconConfig.className}`} />
      ) : null}
    </span>
  ) : null;

  return (
    <div className="relative">
      <div className="flex items-center gap-3 bg-muted rounded-lg p-3">
        <div
          className={
            showFavicon
              ? 'shrink-0 w-10 h-10 flex items-center justify-center'
              : 'shrink-0 w-10 h-10 flex items-center justify-center rounded bg-card border border-border overflow-hidden'
          }
        >
          {showFavicon ? (
            <img
              src={faviconUrl}
              alt=""
              className="h-5 w-5 object-contain"
              onError={() => setFaviconFailed(true)}
            />
          ) : (
            <FileIcon className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
        <div className="grow min-w-0">
          <div className="flex items-baseline gap-1.5 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
            {extraCount && (
              <span className="shrink-0 text-xs font-medium text-foreground-subtle">
                +{extraCount}
              </span>
            )}
          </div>
          {error ? (
            <p className="text-xs text-red-500 mt-0.5">{error}</p>
          ) : (
            fileType === UploadItemTypeEnum.FILE && (
              <p className="text-xs text-foreground-subtle mt-0.5 font-medium">
                {getFileSizeText(size)}
              </p>
            )
          )}
        </div>
        <div className="shrink-0">{statusIcon}</div>
        {showRemoveButton && onRemove && (
          <Button
            variant="custom"
            size="icon"
            onClick={() => onRemove(index)}
            className="absolute top-4 right-2 p-1 rounded-full hover:bg-accent transition-colors !border-0"
            disabled={isPending}
          >
            <X className="h-4 w-4 text-foreground-subtle" />
          </Button>
        )}
      </div>
    </div>
  );
};
